import React from 'react'
import { Box, Typography, alpha } from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'

import { BrandBalance, DomainBalance } from '@/src/types/wallet'
import { flagMap } from '@/src/constants/flagMap'

export interface DomainBalanceCarouselProps {
    displayBalance: number
    displayCurrency: string
    domainBalances: DomainBalance[]
    brandBalances: BrandBalance[]
    selectedDomainIndex: number
    onDomainSelect: (index: number) => void
    formatAmount: (amount: number, currency: string) => string
    t: (key: string, values?: Record<string, any>) => string
    isDesktop?: boolean
    attachBottom?: boolean
}

const CURRENCY_COUNTRY_KEY_MAP: Record<string, string> = {
    SAR: 'saudiArabia',
    AED: 'uae',
    QAR: 'qatar',
    KWD: 'kuwait',
    BHD: 'bahrain',
    OMR: 'oman',
    EGP: 'egypt'
}

const DomainBalanceCarousel: React.FC<DomainBalanceCarouselProps> = ({
    displayBalance,
    displayCurrency,
    domainBalances,
    brandBalances,
    selectedDomainIndex,
    onDomainSelect,
    formatAmount,
    t,
    isDesktop = false,
    attachBottom = false
}) => {
    const hasDomainBalances = domainBalances.length > 0
    const componentRadius = isDesktop ? '16px' : '14px'
    const effectiveBrandBalances = brandBalances.map(bb =>
        bb.brandSource === 'MAIN' && hasDomainBalances
            ? { ...bb, balance: displayBalance, currency: displayCurrency }
            : bb
    )

    return (
        <Box>
            {hasDomainBalances && (
                <Box
                    sx={{
                        display: 'flex',
                        gap: 1.25,
                        pl: isDesktop ? 0 : 0,
                        paddingInlineStart: isDesktop ? 0 : 2.25,
                        scrollPaddingInlineStart: isDesktop ? 0 : 18,
                        pr: isDesktop ? 0.5 : 2,
                        pb: 1.5,
                        overflowX: 'auto',
                        scrollSnapType: 'x mandatory',
                        '&::-webkit-scrollbar': { display: 'none' },
                        msOverflowStyle: 'none',
                        scrollbarWidth: 'none'
                    }}
                >
                    {domainBalances.map((db, index) => {
                        const isSelected = index === selectedDomainIndex
                        const countryKey = CURRENCY_COUNTRY_KEY_MAP[db.currency]
                        const countryName = countryKey ? t(`countryNames.${countryKey}`) : db.domain || db.currency
                        const CurrencyFlag = flagMap[db.currency]

                        return (
                            <Box
                                key={db.domain || db.currency}
                                onClick={() => onDomainSelect(index)}
                                sx={{
                                    scrollSnapAlign: 'start',
                                    minWidth: isDesktop ? 140 : 210,
                                    px: 2,
                                    py: 1.25,
                                    borderRadius: componentRadius,
                                    border: 1.5,
                                    borderColor: isSelected ? 'text.primary' : 'divider',
                                    bgcolor: 'background.paper',
                                    cursor: 'pointer',
                                    flexShrink: 0,
                                    transition: 'all 0.2s ease',
                                    display: 'flex',
                                    flexDirection: 'row',
                                    alignItems: 'center',
                                    gap: 1.25,
                                    '&:hover': {
                                        borderColor: 'text.primary'
                                    }
                                }}
                            >
                                <Box
                                    sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 24 }}
                                >
                                    {CurrencyFlag ? (
                                        <CurrencyFlag width={24} height={18} style={{ borderRadius: 2 }} />
                                    ) : (
                                        <Typography sx={{ fontSize: '1.25rem' }}>🌍</Typography>
                                    )}
                                </Box>
                                <Box sx={{ minWidth: 0, textAlign: 'left' }}>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontWeight: 400,
                                            color: 'text.primary',
                                            fontSize: '14px',
                                            pr: 1,
                                            whiteSpace: 'nowrap'
                                        }}
                                    >
                                        {countryName}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontWeight: 700,
                                            color: 'text.primary',
                                            fontSize: '12px',
                                            pr: 1,
                                            mt: 0.5,
                                            whiteSpace: 'nowrap'
                                        }}
                                    >
                                        {formatAmount(db.balance, db.currency)}
                                    </Typography>
                                </Box>
                            </Box>
                        )
                    })}
                </Box>
            )}

            <Box
                sx={{
                    mx: isDesktop ? 0 : 2,
                    mt: 1.5,
                    p: 2.5,
                    borderRadius: componentRadius,
                    borderBottomLeftRadius: attachBottom ? 0 : undefined,
                    borderBottomRightRadius: attachBottom ? 0 : undefined,
                    bgcolor: 'background.paper',
                    border: 1,
                    borderColor: 'divider'
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.8125rem' }}>
                        {t('walletBalance')}
                    </Typography>
                </Box>

                <Typography
                    variant="h4"
                    sx={{
                        fontWeight: 700,
                        fontSize: isDesktop ? '2rem' : '1.75rem',
                        letterSpacing: '-0.5px',
                        color: 'text.primary',
                        mb: 2
                    }}
                >
                    {formatAmount(displayBalance, displayCurrency)}
                </Typography>

                <Box
                    sx={{
                        borderBottom: '1px dashed',
                        borderColor: 'divider',
                        mb: 2
                    }}
                />

                {effectiveBrandBalances.length > 0 && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {effectiveBrandBalances.map(bb => (
                            <Box
                                key={bb.brandSource}
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center'
                                }}
                            >
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: 'text.secondary',
                                        fontSize: '0.875rem'
                                    }}
                                >
                                    {bb.brandSource === 'MAIN' ? t('brandFilters.flyinWallet') : t('brandFilters.cleartripWallet')}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontWeight: 600,
                                        color: 'text.primary',
                                        fontSize: '0.875rem'
                                    }}
                                >
                                    {formatAmount(bb.balance, bb.currency)}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                )}

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 2 }}>
                    <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                        {t('usableOnlyInCurrency', { currency: displayCurrency })}
                    </Typography>
                </Box>
            </Box>
        </Box>
    )
}

export { DomainBalanceCarousel }
export type { DomainBalanceCarouselProps as BalanceCarouselProps }
